import { Component } from 'react';
import Button from './Button.jsx';

/**
 * Catches render-time crashes so one broken screen doesn't blank the whole
 * app — which matters more in the native shell, where a white screen has no
 * browser reload button to escape it.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[HealthyFruitsBox] render error', error, info);
    this.props.onError?.(error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return typeof this.props.fallback === 'function'
        ? this.props.fallback(error, this.reset)
        : this.props.fallback;
    }

    return (
      <div className="shell flex min-h-[60vh] flex-col items-center justify-center py-16 text-center">
        <span className="text-4xl" aria-hidden="true">
          🍋
        </span>
        <h1 className="mt-4 font-display text-2xl text-heading">This screen didn&apos;t load</h1>
        <p className="mt-2 max-w-md text-sm text-ink-muted">
          Something went wrong on our side. Please try again. If it keeps happening, email{' '}
          <a className="underline" href="mailto:help@healthyfruitsbox.com">
            help@healthyfruitsbox.com
          </a>
          .
        </p>
        {import.meta.env.DEV && (
          <pre className="mt-6 max-w-full overflow-x-auto rounded-2xl bg-oat/60 p-4 text-left text-xs text-ink">
            {String(error?.stack || error?.message || error)}
          </pre>
        )}
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Button onClick={this.reset}>Try again</Button>
          <Button variant="secondary" to="/">
            Back to home
          </Button>
        </div>
      </div>
    );
  }
}
